import React, { Component } from 'react';
import lodashGet from 'lodash/get';
import { Header, Loader } from 'semantic-ui-react';
import { connect } from 'react-redux';

import * as actions from '../../../actions';
import Info from './Info';
import TimeSeriesChart from '../../Charts/TimeSeriesChart';
import { Articles, HotArticles, Users } from '../common';
import { BOARD_ARTICLES_LIMIT } from '../../../constants';
import { TOP_USER_TYPE } from '../common/Users';
import { TwentyFourHourToTwelveHourFormat, formatDatetime } from '../../Utils';
import { selectBoardInfoResults } from '../../../selectors';

const TOTAL_ARTICLES_PAGES = 100;
const HOT_ARTICLES_PAGE_SIZE = 20;
const TOTAL_HOT_ARTICLES_PAGES = 1;
const DEFAULT_HOT_ARTICLES_SINCE_DAYS_AGO = 1;

class Board extends Component {
  constructor(props) {
    super(props);
    const { id } = this.props;

    this.state = {
      id,
    };
  }

  componentDidMount() {
    if (String(this.state.id) !== String(lodashGet(this.props.info, 'id'))) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (String(prevState.id) !== String(this.state.id)) {
      this.fetchData();
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (props.id !== state.id) {
      return { id: props.id };
    }
    return null;
  }

  fetchHotArticles = async (offset, sinceDaysAgo) => {
    await this.props.getBoardHotArticles(
      this.state.id,
      sinceDaysAgo,
      HOT_ARTICLES_PAGE_SIZE,
      offset,
    );
  };

  fetchData = () => {
    const {
      articles,
      getBoardArticles,
      getBoardInfo,
      getBoardTopUsers,
      getBoardTrends,
      getBoardUserCount,
      hotArticles,
    } = this.props;

    const { id } = this.state;

    getBoardInfo(id).then(r => {
      if (r.value?.data) {
        this.props.setSeo({
          title: `PTT | ${r.value.data.title}`,
          description: `PTT 看板 - ${r.value.data.title}: ${r.value.data.headline}`,
        });
      }
    });
    getBoardTrends(id);
    getBoardTopUsers(id);
    getBoardUserCount(id);

    // Based on board id
    this.fetchHotArticles(
      hotArticles.board === id ? hotArticles.offset : 0,
      hotArticles.board === id
        ? hotArticles.sinceDaysAgo
        : DEFAULT_HOT_ARTICLES_SINCE_DAYS_AGO,
    );
    getBoardArticles(
      id,
      BOARD_ARTICLES_LIMIT,
      articles.board === id ? articles.offset : 0,
    );
  };

  fetchMoreArticles = activePage => {
    const { getBoardArticles } = this.props;

    const { id } = this.state;

    const newOffset = (activePage - 1) * BOARD_ARTICLES_LIMIT;
    getBoardArticles(id, BOARD_ARTICLES_LIMIT, newOffset);
  };

  handleHotArticlesDaysAgoDropdown = (e, { value }) =>
    this.fetchHotArticles(0, value);

  handleHotArticlesPagination = (e, { activePage }) => {
    this.fetchHotArticles(
      (activePage - 1) * HOT_ARTICLES_PAGE_SIZE,
      DEFAULT_HOT_ARTICLES_SINCE_DAYS_AGO,
    );
  };

  render() {
    const {
      articles,
      authed,
      hotArticles,
      info,
      isFetching,
      isMobile,
      topUsers,
      trends,
      userCount,
    } = this.props;

    if (isFetching) {
      return <Loader active inline="centered" />;
    }
    if (!info) {
      return <Header as="h1" content="Oops..沒有找到板喔" color="grey" />;
    }

    return (
      <div>
        <Info authed={authed} data={info} isMobile={isMobile} />
        <TimeSeriesChart
          data={userCount.data.map(({ d, count }) => ({
            indexBy: TwentyFourHourToTwelveHourFormat(d),
            value: count,
          }))}
          keys={['value']}
          indexBy="indexBy"
          isFetching={userCount.isFetching}
          isMobile={isMobile}
          color="#4285F4"
          title="平均在線人數"
        />
        <TimeSeriesChart
          data={trends.data.map(({ d, score }) => ({
            indexBy: formatDatetime(d),
            value: score,
          }))}
          keys={['value']}
          indexBy="indexBy"
          isFetching={trends.isFetching}
          isMobile={isMobile}
          title="單日文章數"
        />
        <HotArticles
          isMobile={isMobile}
          isFetching={hotArticles.isFetching}
          data={hotArticles.data}
          offset={hotArticles.offset}
          daysAgo={hotArticles.sinceDaysAgo}
          total={TOTAL_HOT_ARTICLES_PAGES}
          pageSize={HOT_ARTICLES_PAGE_SIZE}
          onPageChange={this.handleHotArticlesPagination}
          onChangeDaysAgoDropdown={this.handleHotArticlesDaysAgoDropdown}
        />
        <Articles
          data={articles.data}
          fetchFunc={this.fetchMoreArticles}
          isFetching={articles.isFetching}
          isMobile={isMobile}
          offset={articles.offset}
          pageSize={BOARD_ARTICLES_LIMIT}
          showAuthor
          showPushStats
          title={`最近${TOTAL_ARTICLES_PAGES * BOARD_ARTICLES_LIMIT}篇文章`}
          total={TOTAL_ARTICLES_PAGES}
        />
        <Users
          authed={authed}
          title="活躍用戶"
          type={TOP_USER_TYPE.ACTIVE}
          data={topUsers.data.filter(user => user.tag === TOP_USER_TYPE.ACTIVE)}
          isFetching={topUsers.isFetching}
        />
        <Users
          authed={authed}
          title="噓文王"
          type={TOP_USER_TYPE.HATES}
          data={topUsers.data.filter(user => user.tag === TOP_USER_TYPE.HATES)}
          isFetching={topUsers.isFetching}
        />
        <Users
          authed={authed}
          title="推文王"
          type={TOP_USER_TYPE.LIKES}
          data={topUsers.data.filter(user => user.tag === TOP_USER_TYPE.LIKES)}
          isFetching={topUsers.isFetching}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  articles: state.board.articles,
  hotArticles: state.board.hotArticles,
  info: selectBoardInfoResults(state),
  isFetching: state.board.info.isFetching,
  isMobile: state.browser.lessThan.medium,
  topUsers: state.board.topUsers,
  trends: state.board.trends,
  userCount: state.board.userCount,
});

export default connect(mapStateToProps, actions)(Board);
